import React, {FunctionComponent, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    ACard,
    AChartX,
    AChartXOption,
    AxisHoverParam,
    AToggleButtonGroup,
    Period,
    PeriodSelector,
    Skeleton,
    XAxis,
    YAxis
} from "@atiautomacao/ati-ui-library";
import IconButton from "@mui/material/IconButton";
import {Box, Divider, ListItemIcon, ListItemText, MenuItem, Stack, Typography, useTheme} from "@mui/material";
import {roundNextHundred, truncateNumber} from "../../../Utils/NumberUtil";
import {addMinutes, differenceInDays, format, parseISO,} from "date-fns";
import {EquipmentEntity} from "./ActivePowerInvertersChartReducer";
import ActivePowerReactQuery from "./ActivePowerReactQuery";
import {checkDiffDays} from "../../../Utils/DataUitils";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import {useAppSelector} from "../../../Config/Hooks";
import {ChangeCircleOutlined, Fullscreen} from "@mui/icons-material";
import { getChartXOptions } from "../../../Utils/ChartXOption";
import ActivePowerInvertersChartModal from "./ActivePowerInvertersChartModal";
import ChartBottomHint from "../../../Shared/Components/ChartBottomHint/ChartBottomHint";

// Curva do gêmeo digital (SGD-3639): série de referência em destaque sobre as curvas dos inversores.
const DIGITAL_TWIN_SERIES_NAME = "Referência";
const DIGITAL_TWIN_COLOR = "#1E293B";

// Legenda própria da Referência (gêmeo digital): fixa, destacada, acima da dos inversores — SGD-3639.
const REFERENCE_LEGEND = {
    show: true,
    orient: 'horizontal' as const,
    bottom: 26,
    left: 'center' as const,
    // Ícone de traço contínuo, coerente com a linha sólida da curva de Referência.
    icon: 'path://M0,0 L29,0 L29,2 L0,2 Z',
    itemWidth: 29,
    itemHeight: 3,
    data: [DIGITAL_TWIN_SERIES_NAME],
    formatter: () => '{ref|Referência}  {ideal|Curva ideal}',
    textStyle: {
        rich: {
            ref: {fontWeight: 'bold' as const, color: DIGITAL_TWIN_COLOR},
            ideal: {color: '#94A3B8'},
        },
    },
};

const CARD_HEIGHT = 363;

const buildCurrentDayPeriod = (date: Date = new Date()): Period => {
    const fromDateTime = new Date(date);
    fromDateTime.setHours(0, 0, 0, 0);
    const toDateTime = new Date(date);
    toDateTime.setHours(23, 59, 59, 999);
    return { groupBy: "day", fromDateTime, toDateTime };
};

interface ActivePowerInvertersChartProps {
    powerStationData: any;
    onDataFound: any;
    refreshTrigger?: number;
    /** Quanto (px) o hint exibido ultrapassa a borda inferior do card; 0 quando oculto. */
    onHintOverflowChange?: (powerStationId: number, overflow: number) => void;
    embedded?: boolean;
    title?: string;
    extraHeaderControls?: ReactNode;
    headerDateControl?: ReactNode;
    period?: Period;
    onPeriodChange?: (period: Period) => void;
    cardHeight?: number;
    ariaLabel?: string;
}

const ActivePowerInvertersChart: FunctionComponent<ActivePowerInvertersChartProps> = ({
    powerStationData,
    onDataFound,
    refreshTrigger,
    onHintOverflowChange,
    embedded = false,
    title,
    extraHeaderControls,
    headerDateControl,
    period: controlledPeriod,
    onPeriodChange,
    cardHeight,
    ariaLabel,
}) =>  {
    // Setting range start period
    const initialPeriod: Period = {
        groupBy: "day",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const isPeriodControlled = controlledPeriod !== undefined;
    const [internalPeriod, setInternalPeriod] = useState<Period>(controlledPeriod ?? initialPeriod);
    const period = isPeriodControlled ? controlledPeriod : internalPeriod;
    const resolvedCardHeight = cardHeight ?? CARD_HEIGHT;
    const cardTitle = title ?? (powerStationData.name ? powerStationData.name : "Nome indefinido");
    const cardAriaLabel = ariaLabel ?? (powerStationData.name ? `dashboard-active-power-${powerStationData.name}` : "Nome indefinido");

    const setPeriod = (next: Period) => {
        onPeriodChange?.(next);
        if (!isPeriodControlled) {
            setInternalPeriod(next);
        }
    };
    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<Array<YAxis>>(Array<YAxis>);
    const [series, setSeries] = useState<Array<any>>(Array<any>);
    const [radioGroup, setRadioGroup] = React.useState('powerPerUnit');
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Hint exibido abaixo do gráfico (SGD-3839), alimentado pelo onAxisHover do AChartX.
    const [hintParams, setHintParams] = useState<AxisHoverParam[] | null>(null);
    // Força ressincronizar o PeriodSelector (que tem state interno só no mount) ao fechar o modal:
    // se o usuário mudou a data no modal, o display do calendário no card precisa refletir o novo period.
    const [periodSelectorKey, setPeriodSelectorKey] = useState(0);
    const handleOpenFullscreen = () => {
        setHintParams(null);
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setPeriodSelectorKey((k) => k + 1);
    };

    const handleDayShortcutClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('[aria-label="Diário"]')) {
            return;
        }
        setPeriod(buildCurrentDayPeriod());
        setPeriodSelectorKey((k) => k + 1);
    };

    const getUnity  = () => {
        return radioGroup.includes("powerPerUnit") ? 'PU' : 'kW';
    }

    const {data,isSuccess, isLoading, isError, refetch} = ActivePowerReactQuery(period,getUnity(),powerStationData.id );
    const [equipmentEntity,setEquipmentEntity] = useState<EquipmentEntity[] | undefined>();

    useEffect(() => {
        // Signal the parent only when this instance's request is finished (success or error),
        // so the sequential loading in the parent chains to the next power station.
        if (!isLoading && (isSuccess || isError)) {
            onDataFound(powerStationData.id);
        }
    }, [isLoading, isSuccess, isError, powerStationData.id, onDataFound]);

    // Auto-refresh: parent bumps refreshTrigger when it's this card's turn
    // to re-fetch, keeping the sequence one-at-a-time. Skip the initial mount
    // (the useQuery already runs on mount). refetch() completing will re-trigger
    // the effect above → onDataFound → parent advances to the next card.
    const firstRefreshTriggerRef = React.useRef<number | undefined>(refreshTrigger);
    useEffect(() => {
        if (refreshTrigger === undefined) return;
        if (firstRefreshTriggerRef.current === refreshTrigger) return;
        firstRefreshTriggerRef.current = refreshTrigger;
        refetch();
    }, [refreshTrigger, refetch]);

    const handleChange = (event: React.MouseEvent<HTMLElement>, value: string | null) => {
        setRadioGroup(value ?? 'powerPerUnit');
    };

    const valueFormatter = (value:any,unity:string) => {
        return  value + ' ' + unity
    }

    const max = () => {
        let maxValues = 0;
        if(equipmentEntity){
            maxValues = Math.max(...equipmentEntity.map(element => element.activePower));
        }

        return truncateNumber(maxValues + (maxValues * 0.15));
    }

    const adjustDataPoints = (dataPoints: any[]): any[] => {
        if (dataPoints.length === 0) {
            return dataPoints;
        }

        // Parseia o primeiro e último dateTime para objetos Date
        const firstDateTime = parseISO(dataPoints[0].dateTime);
        const lastDateTime = parseISO(dataPoints[dataPoints.length - 1].dateTime);
        const powerStationName = dataPoints[0].powerStationName
        const skidName = dataPoints[0].skidName
        const equipmentId = dataPoints[0].equipmentId
        const equipmentName = dataPoints[0].equipmentName

        // Horários limite
        const startTime = new Date(firstDateTime);
        startTime.setHours(4, 0, 0, 0); // 4:00
        const endTime = new Date(lastDateTime);
        endTime.setHours(20, 0, 0, 0); // 20:00

        let newData = [...dataPoints]
        while (firstDateTime > startTime) {
            const newDateTime = addMinutes(firstDateTime, -5);
            newData.unshift({
                dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm:ss.S'),
                activePower: null,
                reactivePower: null,
                powerPerUnit: null,
                equipmentId: equipmentId,
                equipmentName: equipmentName,
                value: null,
                powerStationName: powerStationName,
                skidName: skidName,
            });
            firstDateTime.setTime(newDateTime.getTime());
        }

        while (lastDateTime < endTime) {
            const newDateTime = addMinutes(lastDateTime, 5);
            newData.push({
                dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm:ss.S'),
                activePower: null,
                reactivePower: null,
                powerPerUnit: null,
                equipmentId: equipmentId,
                equipmentName: equipmentName,
                value: null,
                powerStationName: powerStationName,
                skidName: skidName,
            });
            lastDateTime.setTime(newDateTime.getTime());
        }

        return newData;
    }

    const handleAxisY = () => {
        setYAxis([])
        if(radioGroup.includes("powerPerUnit")){
            const defaultRightY = 1200;
            const negativeOffsetRatio = 2 / 30;
            const maxLeftY = 1.5;
            const minLeftY = -truncateNumber(maxLeftY * negativeOffsetRatio, 1);
            const minRightY = -truncateNumber(defaultRightY * negativeOffsetRatio, 0);
            setYAxis([
                {
                    name: 'PU',
                    max: maxLeftY,
                    min: minLeftY,
                    splitNumber: 5,
                    nameLocation: 'middle',
                    nameGap: 30,
                    type: 'value',
                    axisLine: {
                        onZero: true
                    }
                },
                {
                    name: "W/m²",
                    type: 'value',
                    max: defaultRightY,
                    min: minRightY,
                    position: 'right',
                    splitNumber: 5,
                    interval: roundNextHundred(Number((defaultRightY - minRightY).toPrecision(2)) / 4),
                    nameLocation: 'middle',
                    nameGap: 40,
                    splitLine: {
                        show: false
                    }
                }
            ])
        } else {
            const defaultRightY = 1200;
            const maxAxisY = max();
            const minLeftY = maxAxisY > 0 ? -truncateNumber(maxAxisY * 0.05, 2) : 0;
            const minRightY = maxAxisY > 0 && minLeftY < 0
                ? truncateNumber((defaultRightY * minLeftY) / maxAxisY, 2)
                : 0;
            setYAxis([
                {
                    name: 'kW',
                    max: maxAxisY,
                    min: minLeftY,
                    splitNumber: 5,
                    nameLocation: 'middle',
                    nameGap: 40,
                    axisLine: {
                        onZero: true
                    }
                },
                {
                    name: "W/m²",
                    type: 'value',
                    max: defaultRightY, // Ou algum valor máximo que você deseja definir
                    min: minRightY,
                    position: 'right',
                    splitNumber: 5,
                    interval: roundNextHundred((defaultRightY - minRightY) / 4),
                    nameLocation: 'middle',
                    nameGap: 40,
                    splitLine: {
                        show: false
                    }
                }
            ]);
        }
    }

    const findPowerActiveDataByPeriod = () => {
        handleAxisY();
        const equipmentEntitySort = equipmentEntity ? [...equipmentEntity].sort((a, b) => {
            return a.equipmentName.localeCompare(b.equipmentName);
        }) : [];
        // Cada série é indexada por horário (HH:mm) e o eixo X é a UNIÃO ORDENADA de todos os
        // horários. Assim alinhamos por TEMPO séries de fontes distintas — inversores (Postgres)
        // e a curva de Referência/gêmeo (Influx) — que podem ter grades de tempo diferentes.
        // Antes o casamento era posicional: quando o gêmeo trazia horários fora da grade dos
        // inversores, o eixo ficava maior/desordenado e as curvas apareciam "encolhidas". SGD-3639.
        // Arredonda o horário para a grade de 5 min. Os inversores já vêm arredondados do SQL
        // (date_trunc + minuto/5), mas o gêmeo vem cru do Influx em :03:42, :08:42, :13:42...
        // Sem esse snap, os horários das duas fontes nunca coincidem, o eixo intercala os
        // instantes e cada série vira pontos soltos (sem linha). SGD-3639.
        const toHourMinute = (dateTime: any) => {
            const d = new Date(dateTime);
            const totalMinutes = d.getHours() * 60 + d.getMinutes() + Math.round(d.getSeconds() / 60);
            const snapped = Math.round(totalMinutes / 5) * 5;
            const hh = Math.floor(snapped / 60) % 24;
            const mm = snapped % 60;
            return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
        };
        const pickValue = (element: any) => radioGroup.includes("powerPerUnit")
            ? (element.powerPerUnit != null ? truncateNumber(element.powerPerUnit, 2) : '-')
            : (element.activePower != null ? truncateNumber(element.activePower, 2) : '-');

        const equipmentIds = Array.from(new Set(equipmentEntitySort.map(item => item.equipmentId)));
        const perEquipment = equipmentIds.map(element => {
            const filter = equipmentEntitySort.filter(d => d.equipmentId === element);
            const adjusted = adjustDataPoints(filter);
            const isDigitalTwin = filter.some((point: any) => point.digitalTwin === true);
            const valueByTime = new Map<string, any>();
            adjusted.forEach((item: any) => valueByTime.set(toHourMinute(item.dateTime), pickValue(item)));
            return {filter, isDigitalTwin, valueByTime};
        });

        // Irradiância (eixo Y secundário), indexada por horário.
        // IMPORTANTE: a curva de Referência (gêmeo) NÃO carrega irradiância (vem null do Influx) e
        // sua grade de tempo, após o snap, colide com a dos inversores. Se incluíssemos as linhas do
        // gêmeo aqui, o null delas sobrescreveria a irradiância real do inversor no mesmo slot e a
        // irradiância "sumiria" do gráfico. Por isso só consideramos linhas de inversor e nunca
        // deixamos um null sobrescrever um valor já preenchido. SGD-3639.
        const irradianceRows = (data?.data ?? []).filter((d: any) => d.digitalTwin !== true);
        const filteredData = irradianceRows.reduce((acc: any[], current: any) => {
            const x = acc.find(item => item.dateTime === current.dateTime);
            return x ? acc : acc.concat([current]);
        }, []);
        const irradianceArray = filteredData ? adjustDataPoints(filteredData) : [];
        const irradianceByTime = new Map<string, any>();
        irradianceArray.forEach((item: any) => {
            const key = toHourMinute(item.dateTime);
            const value = item.irradiance != null ? truncateNumber(item.irradiance, 2) : '-';
            const existing = irradianceByTime.get(key);
            // não sobrescreve um valor real por '-'
            if (existing === undefined || existing === '-') {
                irradianceByTime.set(key, value);
            }
        });

        // Eixo X = união ORDENADA de todos os horários ("HH:mm" ordena cronologicamente).
        const allTimes = new Set<string>();
        perEquipment.forEach(pe => pe.valueByTime.forEach((_v, t) => allTimes.add(t)));
        irradianceByTime.forEach((_v, t) => allTimes.add(t));
        const datePeriod = Array.from(allTimes).sort();

        setXAxis({name: '', data: datePeriod, min: '04:00', max: '20:00', nameGap: 20, nameLocation: 'center'});

        let dataSeries: Array<any> = [];
        perEquipment.forEach(pe => {
            const seriesData = datePeriod.map(t => pe.valueByTime.has(t) ? pe.valueByTime.get(t) : '-');
            if (pe.isDigitalTwin) {
                // Curva de referência (gêmeo digital): linha destacada, por cima das demais.
                dataSeries.push({
                    name: DIGITAL_TWIN_SERIES_NAME,
                    data: seriesData,
                    type: 'line',
                    unity: getUnity(),
                    smooth: true,
                    showSymbol: false,
                    z: 10,
                    lineStyle: {color: DIGITAL_TWIN_COLOR, width: 2, opacity: 0.6},
                    itemStyle: {color: DIGITAL_TWIN_COLOR},
                    tooltip: {valueFormatter: (value: any) => valueFormatter(value, getUnity()), show: true},
                    yAxisIndex: 0,
                });
            } else {
                dataSeries.push({
                    name: pe.filter[0].equipmentName + " - " + pe.filter[0].skidName,
                    data: seriesData,
                    type: 'line',
                    unity: getUnity(),
                    smooth: true,
                    tooltip: {valueFormatter: (value: any) => valueFormatter(value, getUnity()), show: true},
                    yAxisIndex: 0,
                });
            }
        });

        dataSeries.push({
            name: 'Irradiância',
            type: 'line',
            unity: 'W/m²',
            itemStyle: {normal: {color: '#FEC60F', lineStyle: {width: 1}, smooth: true}},
            yAxisIndex: 1,
            data: datePeriod.map(t => irradianceByTime.has(t) ? irradianceByTime.get(t) : '-'),
            tooltip: {valueFormatter: (value: any) => valueFormatter(value, 'W/m²'), show: true},
            smooth: true,
            areaStyle: {color: 'rgba(254, 198, 15, 0.2)'}
        });
        setSeries(dataSeries)
    }
    useEffect(() => {
           if( period.toDateTime && period.fromDateTime && differenceInDays(period.toDateTime, period.fromDateTime) <= 6 && checkDiffDays(period.toDateTime, period.fromDateTime) > 0){
            if(isSuccess && data){
                setEquipmentEntity(data.data)
            }else{
                setEquipmentEntity([])
            }

        }
    }, [period, radioGroup, data]);


    useEffect(() => {
        if(equipmentEntity && equipmentEntity.length > 0) {
            findPowerActiveDataByPeriod()
        }else {
            setSeries([])
        }
    }, [equipmentEntity, period]);

    const theme = useTheme();
    const hasDigitalTwin = series.some((sr: any) => sr.name === DIGITAL_TWIN_SERIES_NAME);
    // Memoizado: o hint atualiza estado a cada movimento do mouse e um option novo a cada render
    // faria o AChartX chamar setOption continuamente (flicker/tooltip reiniciando). SGD-3839.
    const option = useMemo<AChartXOption>(() => {
        // Legenda paginável dos inversores + irradiância (exclui a Referência, que ganha legenda própria).
        const inverterLegend = {
            show: true,
            orient: 'horizontal' as const,
            type: 'scroll' as const,
            bottom: 2,
            left: 'center' as const,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {opacity: 1},
            itemStyle: {opacity: 1},
            data: series
                .filter(sr => sr.name !== DIGITAL_TWIN_SERIES_NAME)
                .map(sr => sr.name),
        };
        // Tipagem do AChartXOption (EChartOption v4) não cobre legend em array/rich text; cast pontual.
        return {
            ...getChartXOptions({name:`potência-ativa-${powerStationData.name}-`,date:new Date()}),
            legend: hasDigitalTwin ? [inverterLegend, REFERENCE_LEGEND] : inverterLegend,
            toolbox: {
                feature: {
                    saveAsImage: {
                        show: true,
                        name: `potência-ativa-${powerStationData.name}-${format(new Date(), 'yyyy-MM-dd')}`,
                    },
                    dataView: { show: false, readOnly: false },
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: { show: true },
                    magicType: { show: false, type: ['line', 'bar'] },
                },
                right: 45,
            },
            dataZoom: [
                {
                    type: 'inside',
                    throttle: 50
                },
            ],
            grid: {
                containLabel: true,
                top: 25,
                bottom: hasDigitalTwin ? 58 : 40, // espaço extra para a linha da legenda "Referência"
                right: 15,
                left: 15
            },
            label: {
                precision: 1,
                position: 'right',
                valueAnimation: true,
                fontFamily: 'monospace'
            },
            animationDuration: 2000,
            xAxis: xAxis,
            yAxis: yAxis,
            series: series
        } as unknown as AChartXOption;
    }, [powerStationData.name, hasDigitalTwin, xAxis, yAxis, series]);

    const getSeriesUnit = (param: AxisHoverParam): string | undefined => series[param.seriesIndex]?.unity;

    // O card cresce com o hint, mas o espaço que ocupa no grid (slot) mantém a altura base: assim a linha
    // do grid não cresce e a página desloca só os cards abaixo na mesma coluna pelo quanto o card
    // ultrapassa o slot. Medido antes do paint para os cards não "piscarem". SGD-3839.
    const cardSlotRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        if (!onHintOverflowChange) return;
        const slot = cardSlotRef.current;
        const card = slot?.firstElementChild;
        const overflow = slot && card
            ? Math.max(0, Math.ceil(card.getBoundingClientRect().bottom - slot.getBoundingClientRect().bottom))
            : 0;
        onHintOverflowChange(powerStationData.id, overflow);
    }, [hintParams, onHintOverflowChange, powerStationData.id]);
    useEffect(() => () => onHintOverflowChange?.(powerStationData.id, 0), [onHintOverflowChange, powerStationData.id]);

    let contentToRender: JSX.Element;
    if(isLoading) {
        contentToRender = <Skeleton animation="wave" height={250} variant="rounded" width={"100%"}/>;
    } else if(series && series.length > 0 && xAxis && yAxis.length > 0) {
        contentToRender = (
            <>
                <AChartX
                    key={`chart-overview-active-power-${openSubMenu}`}
                    height={250}
                    option={option}
                    theme={theme.palette.mode}
                    onAxisHover={setHintParams}
                    onChartClick={handleOpenFullscreen}
                />
                {/* mb igual ao padding lateral do conteúdo do card (16px), que não tem padding inferior. */}
                <ChartBottomHint params={hintParams} getUnit={getSeriesUnit} sx={{mb: 2}}/>
            </>
        );
    } else {
        // Altura explícita: o conteúdo do card usa minHeight, então o height 'inherit' do DataNotFound não centraliza.
        contentToRender = <DataNotFound boxStyle={{height: 250}}/>;
    }

    return (
        <Box ref={cardSlotRef} sx={{height: resolvedCardHeight}}>
            <ACard
                ariaLabel={cardAriaLabel}
                title={cardTitle}
                headerControlsPosition={"header"}
                styleProps={{
                   // Altura mínima (não fixa): o card cresce enquanto o hint é exibido abaixo do gráfico. SGD-3839.
                   // No embed da Visão Usina a altura é a do card da usina (463), sem deslocar vizinhos.
                   cardStyle: embedded
                       ? { height: resolvedCardHeight }
                       : { height: 'auto', minHeight: CARD_HEIGHT},
                    contentStyle: { padding: '0 16px', minHeight: 250, alignItems: 'center'},
                    titleStyle: { alignItems: 'center', display: 'flex' },
                    subTitleStyle: { display: 'none' },
                    headerStyle: { flexDirection: 'row' },
                    headerActionsStyle: { alignItems: 'center', display: 'flex'},
                }}
                headerControls={
                    embedded ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                            {extraHeaderControls}
                            {headerDateControl}
                        </Box>
                    ) : (
                        <Box
                            onClick={handleDayShortcutClick}
                            sx={{ display: 'contents' }}
                        >
                            <PeriodSelector
                                key={periodSelectorKey}
                                styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0'}}}
                                mode="hybrid"
                                disableHourSelector={true}
                                inputFormat={'dd/MM/yyyy'}
                                hideDatetime={false}
                                hideGroup={false}
                                periodInterval={{
                                    day: {date: period.toDateTime, startTime: "00:00:00", endTime: "23:59:59"},
                                }}

                                period={period}
                                onChange={setPeriod}
                                groupByOptions={["day"]}
                            />
                        </Box>
                    )
                }
                headerActions={
                    <Stack direction="column" spacing={"8px"}>
                        <MenuItem onClick={handleOpenFullscreen} sx={{borderRadius: '4px'}}>
                            <ListItemIcon>
                                <Fullscreen fontSize="small"/>
                            </ListItemIcon>
                            <ListItemText>Ver em tela cheia</ListItemText>
                        </MenuItem>
                        <Divider/>
                        <Box sx={{ display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: '32px' }}>
                                <IconButton sx={{borderRadius: '4px'}} size="small" onClick={() => setRadioGroup("powerPerUnit")}>
                                    <ChangeCircleOutlined  sx={{marginRight: '12px'}}/>
                                    <Typography variant="body2">Alterar visão</Typography>
                                </IconButton>
                           
                            </Box>
                            <AToggleButtonGroup
                                styleProps={{
                                    toggleButtonGroup: {
                                        paddingLeft: '4px',
                                        height: '30px',
                                    },
                                    toggleButton: {
                                        height: '30px',
                                    },
                                    typography: {
                                        fontSize: '14px',
                                    },
                                }}
                                options={[
                                    { value: "powerPerUnit", label: 'PU' },
                                    { value: "activePower", label: 'KW' },
                                ]}
                                value={radioGroup}
                                exclusive
                                onChange={handleChange}
                            />
                        </Box>
                        <Divider />
                    </Stack>
                }           
            >
                {contentToRender}
                <ActivePowerInvertersChartModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    powerStationName={powerStationData.name ? powerStationData.name : "Nome indefinido"}
                    period={period}
                    onPeriodChange={setPeriod}
                    option={option}
                    isLoading={isLoading}
                    getSeriesUnit={getSeriesUnit}
                    hasContent={Boolean(series && series.length > 0 && xAxis && yAxis.length > 0)}
                />
            </ACard>
        </Box>
    );
}
export default ActivePowerInvertersChart;

